import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingDown, TrendingUp, Minus, Users, Building2, Zap, Sparkles, Send, MessageSquare, ChevronDown, Share2, Mail, FileText, CheckCircle2, Target } from 'lucide-react';
import { Toast } from './Toast';
import { useKPIs } from '../contexts/KPIContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
export interface Insight {
  id: string;
  title: string;
  description: string;
  impactScore: number; // negative means risk reduction
  effortLevel: 'Low' | 'Medium' | 'High';
  actionValue: 'Top Priority' | 'Strategic' | 'Quick Win' | 'Deprioritize';
  affectedSites: number;
  affectedWorkers: number;
  trend: 'worsening' | 'stable' | 'improving';
  suggestedAction: string;
  category: 'ptp' | 'weather' | 'contractor' | 'inspection' | 'positive';
  supportingData: string[];
}
interface InsightDetailModalProps {
  insight: Insight | null;
  onClose: () => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}
export function InsightDetailModal({
  insight,
  onClose
}: InsightDetailModalProps) {
  useEscapeKey(onClose, !!insight);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const {
    addKPIFromInsight
  } = useKPIs();
  useEffect(() => {
    if (insight && messages.length === 0) {
      // Initial AI greeting
      setMessages([{
        id: '1',
        role: 'assistant',
        text: `I identified that ${insight.title.toLowerCase()}. This correlates with higher incident rates. What would you like to explore?`
      }]);
    }
  }, [insight]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (!insight) return null;
  const getTrendIcon = (trend: Insight['trend']) => {
    switch (trend) {
      case 'worsening':
        return <TrendingUp size={16} className="text-red-500" data-id="element-617" data-mp="12xgmp0g7e115" />;
      case 'improving':
        return <TrendingDown size={16} className="text-emerald-500" data-id="element-618" data-mp="12zt4ndlyobe7" />;
      case 'stable':
        return <Minus size={16} className="text-gray-500" data-id="element-619" data-mp="1gv5bgjde5sbh" />;
    }
  };
  const getActionValueColor = (value: Insight['actionValue']) => {
    switch (value) {
      case 'Top Priority':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'Quick Win':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Strategic':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Deprioritize':
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const quickPrompts = ['Why is this happening?', 'Who is most affected?', 'What is the cost of inaction?', 'Draft an action plan'];
  const generateMockResponse = (userQuery: string): string => {
    const lowerQuery = userQuery.toLowerCase();
    if (lowerQuery.includes('why')) {
      return `Based on the data, there are three main factors driving this:\n1. Schedule compression leading to rushed procedures\n2. Recent influx of new workers (<30 days on site)\n3. Inconsistent enforcement across different shifts`;
    }
    if (lowerQuery.includes('who') || lowerQuery.includes('affected')) {
      return `This issue primarily affects SteelCo Inc and AV Mechanic LLC crews across ${insight.affectedSites} sites. The highest concentration is among workers with less than 6 months of experience.`;
    }
    if (lowerQuery.includes('cost') || lowerQuery.includes('inaction')) {
      return `Based on historical incident data, continuing this trend could lead to an estimated $18,000-$24,000 in monthly indirect costs (delays, rework, minor incidents). Addressing it now has a high ROI.`;
    }
    if (lowerQuery.includes('draft') || lowerQuery.includes('plan')) {
      return `Here is a draft action plan:\n\n**Phase 1 (Next 48h):**\n- Issue site-wide safety bulletin\n- Require supervisor sign-off on all related tasks\n\n**Phase 2 (Next Week):**\n- Conduct targeted toolbox talks\n- Review metrics to ensure compliance`;
    }
    return `I've analyzed the data across your projects. Implementing the suggested action ("${insight.suggestedAction}") is estimated to reduce overall risk by ${Math.abs(insight.impactScore)}%.`;
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: generateMockResponse(text)
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1500);
  };
  const handleActionClick = (action: string) => {
    setToastMessage(`${action} initiated`);
    setShowToast(true);
    setShowShareMenu(false);
  };
  return <AnimatePresence data-id="element-620" data-mp="31fs9gg9clcm0">
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-621" data-mp="31g1zg3ue7jrp">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-622" data-mp="cmem9ic6ur70" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-623" data-mp="28pjdawvdz5gi">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-624" data-mp="23s1cxuvfpyk4">
            <div className="flex items-center gap-3" data-id="element-625" data-mp="1z0j8gwu7dfue">
              <div className="p-2 bg-indigo-100 rounded-lg" data-id="element-626" data-mp="1yyyf3ajmk2y4">
                <Sparkles size={20} className="text-indigo-600" data-id="element-627" data-mp="1b9vz074n2i5q" />
              </div>
              <div data-id="element-628" data-mp="16b2liepcqr5k">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-629" data-mp="16bkpxym64bok">
                  Company Insight
                </h2>
                <p className="text-sm text-gray-500" data-id="element-630" data-mp="3hxct4dbtoa9f">Strategic risk analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-631" data-mp="38yreuuddvheg">
              <div className="relative" ref={shareMenuRef} data-id="element-632" data-mp="3902fwmfg2b1v">
                <button onClick={() => setShowShareMenu(!showShareMenu)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-633" data-mp="3dxm4og9rq682">
                  <Share2 size={16} data-id="element-634" data-mp="3rp93onv3hsuj" />
                  Take Action
                  <ChevronDown size={14} data-id="element-635" data-mp="3rp0sr1ij8xlz" />
                </button>

                <AnimatePresence data-id="element-636" data-mp="hwmi8u7pfem">
                  {showShareMenu && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-10" data-id="element-637" data-mp="2gadiej1y0t50">
                      <button onClick={() => handleActionClick('Drafting email')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-638" data-mp="2y1bs90td8orz">
                        <Mail size={16} className="text-gray-400" data-id="element-639" data-mp="3lwy3p2srm4st" />
                        Draft email to team
                      </button>
                      <button onClick={() => handleActionClick('Creating policy memo')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-640" data-mp="3gy378y1vkhq8">
                        <FileText size={16} className="text-gray-400" data-id="element-641" data-mp="lsdcm9h56hiy" />
                        Create policy memo
                      </button>
                      <button onClick={() => handleActionClick('Generating report')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-642" data-mp="uqp92s4ow9jb">
                        <CheckCircle2 size={16} className="text-gray-400" data-id="element-643" data-mp="14nbsdchdasj8" />
                        Assign to safety team
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-644" data-mp="1ifhzya6gfr72">
                <X size={20} data-id="element-645" data-mp="1sdxrufdb34hm" />
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden" data-id="element-646" data-mp="8knjxyz12n3b">
            {/* Left Column: Insight Details */}
            <div className="w-full md:w-1/2 border-r border-gray-200 overflow-y-auto p-6 bg-white" data-id="element-647" data-mp="cmikh194wwg1">
              <div className="mb-6" data-id="element-648" data-mp="hk0jnzxcnxau">
                <div className="flex items-center gap-2 mb-3" data-id="element-649" data-mp="hiz87ucf98sl">
                  <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${getActionValueColor(insight.actionValue)}`} data-id="element-650" data-mp="mcah010snzn5">
                    {insight.actionValue}
                  </span>
                  <span className="px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full border border-gray-200" data-id="element-651" data-mp="wbj90oc3n46v">
                    Effort: {insight.effortLevel}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-652" data-mp="3236j4nu1bygq">
                  {insight.title}
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-653" data-mp="3729j3tv5rehu">{insight.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-654" data-mp="2zja21uv65z1n">
                  <div className={`p-4 rounded-xl border ${insight.impactScore > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-200'}`} data-id="element-655" data-mp="2urs31ccpi8jb">
                    <p className={`text-xs font-semibold mb-1 uppercase tracking-wider ${insight.impactScore > 0 ? 'text-emerald-800' : 'text-amber-800'}`} data-id="element-656" data-mp="2ktc7mx6ncd40">
                      {insight.impactScore > 0 ? 'Risk Improved' : 'Can Reduce Risk By'}
                    </p>
                    <div className="flex items-baseline gap-1" data-id="element-657" data-mp="38h3ma7fnt28g">
                      <span className={`text-3xl font-bold ${insight.impactScore > 0 ? 'text-emerald-600' : 'text-amber-700'}`} data-id="element-658" data-mp="34i366wokgdp5">
                        {insight.impactScore > 0 ? '+' : ''}
                        {Math.abs(insight.impactScore)}%
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-659" data-mp="1ziet5kmb6q7h">
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider" data-id="element-660" data-mp="1zg29n30wk7sb">
                      Trend
                    </p>
                    <div className="flex items-center gap-2" data-id="element-661" data-mp="1pixlwyres49e">
                      {getTrendIcon(insight.trend)}
                      <span className="text-lg font-bold text-gray-900 capitalize" data-id="element-662" data-mp="1br9db5gdbxx6">
                        {insight.trend}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 mb-8 py-4 border-y border-gray-100" data-id="element-663" data-mp="11stiosi4u6ix">
                  <div className="flex items-center gap-2" data-id="element-664" data-mp="16rmywsot0bms">
                    <Building2 size={18} className="text-gray-400" data-id="element-665" data-mp="sv13doceuyvj" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-666" data-mp="wvbj3sq093nl">
                      {insight.affectedSites} Sites Affected
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-667" data-mp="98693xglej2u">
                    <Users size={18} className="text-gray-400" data-id="element-668" data-mp="99r4tqen882a" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-669" data-mp="n87la92b5cxm">
                      {insight.affectedWorkers} Workers
                    </span>
                  </div>
                </div>

                <div className="mb-6" data-id="element-670" data-mp="x5byhje6dy2a">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-671" data-mp="x4an1dt8z9k1">
                    <Zap size={16} className="text-amber-500" data-id="element-672" data-mp="s6s9tb876868" />
                    Suggested Action
                  </h4>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-673" data-mp="1609sktt27gv7">
                    <p className="text-sm font-medium text-amber-900 mb-4" data-id="element-674" data-mp="11yybjco943ym">
                      {insight.suggestedAction}
                    </p>
                    <button onClick={() => {
                    addKPIFromInsight(insight);
                    setToastMessage('Added to KPI tracker');
                    setShowToast(true);
                  }} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-200 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors" data-id="element-675" data-mp="1aybcfgyb7k7q">
                      <Target size={14} data-id="element-676" data-mp="28svv4fh5kx1h" />
                      Track as KPI
                    </button>
                  </div>
                </div>

                <div data-id="element-677" data-mp="1g3f3hk1cxk6b">
                  <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-678" data-mp="1g2vvav1iea5h">
                    Supporting Data
                  </h4>
                  <ul className="space-y-2" data-id="element-679" data-mp="1q0jtxw28k1sx">
                    {insight.supportingData.map((data, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-680" data-mp="2wgvu22ombtr5" data-mpi={idx}>
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" data-id="element-681" data-mp="31htbue5puls4" />
                        {data}
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column: AI Chat */}
            <div className="w-full md:w-1/2 flex flex-col bg-gray-50" data-id="element-682" data-mp="g0h12fs4y02o">
              <div className="p-4 border-b border-gray-200 bg-white shadow-sm z-10" data-id="element-683" data-mp="b2p95lrhqdyh">
                <div className="flex items-center gap-2" data-id="element-684" data-mp="72wy92emjvpy">
                  <Sparkles size={18} className="text-indigo-600" data-id="element-685" data-mp="22sjphxa1b2d" />
                  <h3 className="font-bold text-gray-900" data-id="element-686" data-mp="21hfxi3u03b9">
                    Explore with Bilton AI
                  </h3>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-687" data-mp="3oainnnic089h">
                  Ask questions to dig deeper into this insight.
                </p>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-688" data-mp="1mi48uxb33jxa">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-689" data-mp="3gykout3j3pwz">
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'}`} data-id="element-690" data-mp="370y9k5ibhwch">
                      {msg.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1.5 text-indigo-600" data-id="element-691" data-mp="8jkk5tn0ju4x">
                          <Sparkles size={12} data-id="element-692" data-mp="3qoxhz9pesreq" />
                          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-693" data-mp="3qo4dyghu4g0a">
                            Bilton AI
                          </span>
                        </div>}
                      <div className="whitespace-pre-wrap leading-relaxed" data-id="element-694" data-mp="2fbldcwlts4ri">
                        {msg.text}
                      </div>
                    </div>
                  </div>)}

                {isTyping && <div className="flex justify-start" data-id="element-695" data-mp="26szv625mx86e">
                    <div className="bg-white border border-gray-200 p-4 rounded-2xl rounded-tl-sm shadow-sm flex gap-1" data-id="element-696" data-mp="26s8ctt11t6y2">
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" data-id="element-697" data-mp="2gqo35ts94c6i" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-698" data-mp="2gotga9xf69zs" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-699" data-mp="22r4yjaynfiyy" />
                    </div>
                  </div>}
                <div ref={messagesEndRef} data-id="element-700" data-mp="15apo99ae6vxd" />
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t border-gray-200" data-id="element-701" data-mp="jt7kl8h11sng">
                {/* Quick Prompts */}
                <div className="flex flex-wrap gap-2 mb-3" data-id="element-702" data-mp="os27ali481cu">
                  {quickPrompts.map((prompt, idx) => <button key={idx} onClick={() => handleSend(prompt)} className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full transition-colors whitespace-nowrap" data-id="element-703" data-mp="tp2b0prhss0s" data-mpi={idx}>
                      {prompt}
                    </button>)}
                </div>

                <div className="relative" data-id="element-704" data-mp="1ciy3rvxinkqf">
                  <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(query)} placeholder="Ask about this insight..." className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" data-id="element-705" data-mp="1hgg6h4kxx3pz" />
                  <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors" data-id="element-706" data-mp="31xgwmmefwbrw">
                    <Send size={16} data-id="element-707" data-mp="3ppnwvwnad2wl" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-708" data-mp="3fwymz67dvx3d" />
    </AnimatePresence>;
}